import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Colors, Spacing } from '@/shared/theme';
import { Button, Title, Text, CenteredColumn } from '@/shared/ui';
import { Modal } from '@/shared/Modal';
import supportIcon from '@/assets/images/support.svg';
import { useAnalytics } from '@/hooks/useAnalytics';
import { chatBotManager } from '@/services/chatBotManager';
import { env } from '@/env';
import {
  POPUP_VIEW_SUPPORT,
  EMAIL_SUPPORT,
  CALL_SUPPORT,
  SUPPORT_CLOSE,
  CHATBOT,
} from '@/consts/events';

interface SupportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${Spacing.x2}px;
  width: 100%;
`;

const IconImage = styled.img`
  width: 64px;
  height: 64px;
`;

const Divider = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${Spacing.x2}px;
  margin: ${Spacing.x1}px 0;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${Colors.foregroundDisabledAlt};
  }
`;

const ButtonsRow = styled.div`
  display: flex;
  gap: ${Spacing.x2}px;
  width: 100%;
  margin-top: ${Spacing.x1}px;

  /* Make buttons take equal width and stretch */
  & > button {
    flex: 1;
    width: auto;
  }
`;

const ChatbotButtonContainer = styled.div`
  width: 100%;
  margin-top: ${Spacing.x2}px;
`;

const SupportModal: React.FC<SupportModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { trackView, track, trackPopupView } = useAnalytics();
  const promoCode = env.ACCESS_PILOT_PROMO_CODE;

  useEffect(() => {
    if (isOpen) {
      trackPopupView({ event: POPUP_VIEW_SUPPORT });
    }
  }, [isOpen, trackView]);

  const handleEmailClick = () => {
    track({ event: EMAIL_SUPPORT });
    const supportEmail = env.SUPPORT_EMAIL.replace('%@', promoCode);
    window.location.href = `mailto:${supportEmail}`;
  };

  const handlePhoneClick = () => {
    track({ event: CALL_SUPPORT });
    window.location.href = `tel:${env.SUPPORT_NUMBER}`;
  };

  const handleChatbotClick = () => {
    track({ event: CHATBOT });
    chatBotManager.open();
    onClose();
  };

  const handleClose = () => {
    track({ event: SUPPORT_CLOSE });
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      ariaLabelledBy="support-modal-title"
      ariaDescribedBy="support-modal-description"
    >
      <ModalContent>
        <IconImage src={supportIcon} alt="" aria-hidden="true" />
        <Title size="h4" bold centered id="support-modal-title">
          {t('modals.support-modal.title')}
        </Title>
        <Text size="p2" centered id="support-modal-description">
          {t('modals.support-modal.subtitle')}
        </Text>

        <ChatbotButtonContainer>
          <CenteredColumn span={6}>
            <Button
              variant="action-primary"
              text={t('modals.support-modal.cta-chatbot')}
              onClick={handleChatbotClick}
            />
          </CenteredColumn>
        </ChatbotButtonContainer>

        <Divider aria-hidden="true">
          <Text size="p3">{t('modals.support-modal.or')}</Text>
        </Divider>
        <Text size="p3" centered>
          {t('modals.support-modal.human-operating-hours')}
        </Text>
        <ButtonsRow>
          <Button
            size="xsmall"
            variant="action-secondary"
            text={t('modals.support-modal.cta-email')}
            onClick={handleEmailClick}
          />
          <Button
            size="xsmall"
            variant="action-secondary"
            text={t('modals.support-modal.cta-phone')}
            onClick={handlePhoneClick}
          />
        </ButtonsRow>
      </ModalContent>
    </Modal>
  );
};

export { SupportModal };
